import { ProductSupplierOrderChannel, ProductSupplierPurchaseOrderDoc } from '@gooduncles/gu-app-schema';
import { ColDef, IsRowSelectable, RowClassParams, RowStyle } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { FC, useMemo } from 'react';
import { orderChannelOptions } from 'src/constants';

import { onCellValueChangedWithUpdate, onValueSetterWithValidation } from 'src/utils/ag-grid-util';
import { formatDate } from 'src/utils/date-util';
import {
  MessageWrapper,
  PurchaseOrderWithProductSupplier,
  onPurchaseOrderMessageBodyChanged,
} from 'src/utils/supplier-purchase-order-util';

import { AgCellCopyValue } from 'src/atomic-components/atoms/AgCell/AgCellCopyValue';
import AgCellDownloadPurchaseOrderExcel from 'src/atomic-components/atoms/AgCell/AgCellDownloadPurchaseOrderExcel';
import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';
import { AgCellSendPurchaseOrderSms } from 'src/atomic-components/atoms/AgCell/AgCellSendPurchaseOrderSms';
import { AgCellTransactionHistory } from 'src/atomic-components/atoms/AgCell/AgCellTransactionHistory';

const onCellValueChanged = onCellValueChangedWithUpdate<ProductSupplierPurchaseOrderDoc>('발주서');
const productSupplierPurchaseOrderPath = 'productSupplierPurchaseOrder';

type RowData = PurchaseOrderWithProductSupplier;
interface PurchaseOrderTableProps {
  rowData: RowData[];
  setSelectedRows: (rows: RowData[]) => void;
  showHeader: boolean;
  messageWrapper: MessageWrapper;
}

const defaultColDef: ColDef<RowData> = {
  sortable: true,
  resizable: true,
  filter: true,
};

const getRowStyle = (params: RowClassParams<RowData>): RowStyle | undefined => {
  const data = params.data as RowData;
  if (!data.productSupplier) {
    return { backgroundColor: 'var(--red100)' };
  }
};

const PurchaseMethodRenderer = (props: any) => (
  <span>{orderChannelOptions.find((option) => option.value === props.value)?.label}</span>
);

const SupplierPurchaseOrderTable: FC<PurchaseOrderTableProps> = ({
  rowData,
  setSelectedRows,
  showHeader,
  messageWrapper,
}) => {
  const isRowSelectable: IsRowSelectable<RowData> = useMemo(() => {
    return (params) => {
      return !!params.data && [ProductSupplierOrderChannel.SMS].includes(params.data.orderChannel);
    };
  }, []);

  const columnDefs: ColDef<RowData>[] = useMemo(() => {
    return [
      {
        field: '_id',
        hide: true,
      },
      {
        field: '_timeUpdate',
        hide: true,
      },
      {
        headerName: '거래처ID',
        field: 'supplierId',
        hide: true,
      },
      {
        headerName: '거래처',
        field: 'productSupplier.name',
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        width: 200,
      },
      {
        headerName: '발주서 내용',
        field: 'messageBody',
        width: 254,
        wrapText: true,
        autoHeight: true,
        cellStyle: { whiteSpace: 'pre-wrap', lineHeight: '1.5', padding: 8 },
        editable: true,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorParams: {
          maxLength: 3000,
        },
        cellEditorPopup: true,
        valueFormatter: (params) => {
          const purchaseOrder = params.data as RowData;
          const { messagePrefix, messageSuffix } = params.context.messageWrapper;
          if (!purchaseOrder?.productSupplier) return '';
          // 전산 방식은 불필요한 메시지를 제거한다.
          if (purchaseOrder.productSupplier.orderChannel === 'api') {
            return '';
          }
          return showHeader ? `${messagePrefix}${params.value}${messageSuffix}` : params.value;
        },
        valueSetter: onValueSetterWithValidation,
        onCellValueChanged: onPurchaseOrderMessageBodyChanged,
      },
      {
        headerName: '개별 발송',
        width: 110,
        filter: false,
        field: '_id',
        cellRenderer: AgCellSendPurchaseOrderSms,
        cellRendererParams: (params: any): any => {
          const purchaseOrder = params.data as RowData;
          return {
            purchaseOrder,
            productSupplier: purchaseOrder.productSupplier,
          };
        },
      },
      {
        headerName: '거래처 번호',
        width: 160,
        field: 'productSupplier.primaryContact',
      },
      {
        headerName: '거래처 추가번호',
        width: 160,
        field: 'productSupplier.additionalContacts',
        valueGetter: (params): any => {
          const purchaseOrder = params.data as RowData;
          return purchaseOrder.productSupplier?.additionalContacts.map((contact) => contact.number).join(', ');
        },
      },
      {
        headerName: '발주서 복사',
        filter: false,
        maxWidth: 164,
        minWidth: 138,
        valueGetter: (params): any => {
          const purchaseOrder = params.data as RowData;
          const { messagePrefix, messageSuffix } = params.context.messageWrapper;
          if (!purchaseOrder?.productSupplier) return null;
          if (purchaseOrder.productSupplier.orderChannel === 'api') {
            return purchaseOrder.messageRawList;
          }
          return `${messagePrefix}${purchaseOrder.messageBody}${messageSuffix}`;
        },
        cellRenderer: (params: any): any => {
          const purchaseOrder = params.data as RowData;
          return purchaseOrder.productSupplier?.orderChannel === 'api' ? (
            <AgCellDownloadPurchaseOrderExcel purchaseOrderId={purchaseOrder._id} purchaseOrder={purchaseOrder} />
          ) : (
            <AgCellCopyValue {...params} />
          );
        },
        cellRendererParams: (params: any): any => {
          const purchaseOrder = params.data as RowData;
          const { date } = params.context.messageWrapper;
          if (!purchaseOrder?.productSupplier) return null;
          return {
            name:
              purchaseOrder.productSupplier.orderChannel === 'api'
                ? `${purchaseOrder.productSupplier.name}-${date}`
                : `거래처 '${purchaseOrder.productSupplier.name}' 발주서`,
          };
        },
      },
      {
        headerName: '발주채널',
        field: 'orderChannel',
        width: 110,
        editable: true,
        cellEditorPopup: true,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: Object.keys(ProductSupplierOrderChannel),
          cellRenderer: PurchaseMethodRenderer,
        },
        filterParams: {
          valueFormatter: (params: any) => orderChannelOptions.find((option) => option.value === params.value)?.label,
        },
        cellRenderer: PurchaseMethodRenderer,
        valueSetter: onValueSetterWithValidation,
        onCellValueChanged: (params: any) => onCellValueChanged(params, productSupplierPurchaseOrderPath),
      },
      {
        headerName: '발주서 수정일',
        field: 'messageBodyEditedAt',
        width: 200,
        cellRenderer: AgCellTransactionHistory,
      },
      {
        headerName: '발송요청 여부',
        field: 'messageSendRequests',
        width: 180,
        wrapText: true,
        autoHeight: true,
        cellStyle: { whiteSpace: 'pre-wrap', lineHeight: '1.5', padding: 8 },
        valueFormatter: (params) => {
          const purchaseOrder = params.data as RowData;
          return purchaseOrder.messageSendRequests
            ? purchaseOrder.messageSendRequests
                .map((request) => formatDate(request.requestedAt, 'L월 dd일(EEEEEE) HH:mm:ss'))
                .join('\n')
            : '';
        },
      },
      {
        headerName: '액션',
        hide: true,
        width: 120,
        cellRenderer: DownloadRawDataCell,
      },
    ];
  }, [showHeader]);

  return (
    <div className='ag-theme-alpine height100'>
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowStyle={getRowStyle}
        getRowId={(params) => params.data._id}
        context={{ messageWrapper }}
        enableCellChangeFlash={true}
        // 편집 완료후 스크롤 이동을 막는다.
        suppressScrollOnNewData={true}
        alwaysShowHorizontalScroll={true}
        // 툴팁의 노출 속도
        tooltipShowDelay={0}
        // 행 선택
        rowSelection={'multiple'}
        // 행 선택을 켜놓더라도 개별 셀의 내용을 복사할 수 있도록한다.
        enableCellTextSelection={true}
        isRowSelectable={isRowSelectable}
        onSelectionChanged={(e) => setSelectedRows(e.api.getSelectedRows())}
        onCellValueChanged={(e) => {
          const changedCell = e;
          if (changedCell.colDef.field === 'orderChannel') {
            e.api.redrawRows({ rowNodes: [e.node] });
          }
        }}
      />
    </div>
  );
};

export default SupplierPurchaseOrderTable;
